"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";

const MapContainer = dynamic(
  () => import("react-leaflet").then((mod) => mod.MapContainer),
  { ssr: false }
);

const TileLayer = dynamic(
  () => import("react-leaflet").then((mod) => mod.TileLayer),
  { ssr: false }
);

const Marker = dynamic(
  () => import("react-leaflet").then((mod) => mod.Marker),
  { ssr: false }
);

interface MapPinPickerProps {
  initialLat?: number;
  initialLng?: number;
  initialZoom?: number;
  address?: string;
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
  onChange?: (
    lat: number,
    lng: number,
    address: string,
    pincode: string,
    city: string
  ) => void;
}

// Define types for Leaflet
type LatLngExpression = [number, number] | { lat: number; lng: number };

const MapPinPicker: React.FC<MapPinPickerProps> = ({
  initialLat = 28.6139,
  initialLng = 77.209,
  initialZoom = 10,
  onChange,
}) => {
  const [position, setPosition] = useState<LatLngExpression | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    initialLat,
    initialLng,
  ]);
  const [address, setAddress] = useState<string>("");
  const [pincode, setPincode] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [isClient, setIsClient] = useState(false);
  const [isLeafletLoaded, setIsLeafletLoaded] = useState(false);
  const [MapClickHandler, setMapClickHandler] = useState<React.FC | null>(null);

  // 🔹 Load Leaflet and its components on client only
  // useEffect(() => {
  //   setIsClient(true);
    
  //   const loadLeaflet = async () => {
  //     try {
  //       // Dynamically import leaflet and its CSS
  //       const [leaflet, leafletCss] = await Promise.all([
  //         import("leaflet"),
  //         import("leaflet/dist/leaflet.css")
  //       ]);
        
  //       const L = leaflet.default;
        
  //       // Set up Leaflet marker icon
  //       L.Marker.prototype.options.icon = L.icon({
  //         iconUrl: "/leaflet/marker-icon.png",
  //         iconRetinaUrl: "/leaflet/marker-icon-2x.png",
  //         shadowUrl: "/leaflet/marker-shadow.png",
  //         iconSize: [25, 41],
  //         iconAnchor: [12, 41],
  //       });
        
  //       // Dynamically import useMapEvents and create the click handler component
  //       const { useMapEvents } = await import("react-leaflet");
        
  //       const ClickHandlerComponent: React.FC = () => {
  //         useMapEvents({
  //           click(e: any) {
  //             setPosition([e.latlng.lat, e.latlng.lng]);
  //             onChange?.(e.latlng.lat, e.latlng.lng, address, pincode, city);

  //             getAddress(e.latlng.lat, e.latlng.lng).then((res) => {
  //               if (res) {
  //                 console.log(res);
  //                 setAddress(res.displayName);
  //                 setCity(res.address.city || res.address.town || "");
  //                 setPincode(res.address.postcode || "");
  //               }
  //             });
  //           },
  //         });
  //         return null;
  //       };
        
  //       setMapClickHandler(() => ClickHandlerComponent);
  //       setIsLeafletLoaded(true);
  //     } catch (error) {
  //       console.error("Failed to load Leaflet:", error);
  //     }
  //   };
    
  //   loadLeaflet();
  // }, [onChange, address, pincode, city]);

  useEffect(() => {
    setIsClient(true);
    
    const loadLeaflet = async () => {
      try {
        // Dynamically import leaflet and YOUR CUSTOM leaflet.css
        const [leaflet, leafletCss] = await Promise.all([
          import("leaflet"),
          import("../app/leaflet.css")  // Your custom CSS
        ]);
        
        const L = leaflet.default;
        
        // Set up Leaflet marker icon
        L.Marker.prototype.options.icon = L.icon({
          iconUrl: "/leaflet/marker-icon.png",
          iconRetinaUrl: "/leaflet/marker-icon-2x.png",
          shadowUrl: "/leaflet/marker-shadow.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
        });
        
        // Dynamically import useMapEvents and create the click handler component
        const { useMapEvents } = await import("react-leaflet");
        
        const ClickHandlerComponent: React.FC = () => {
          useMapEvents({
            click(e: any) {
              setPosition([e.latlng.lat, e.latlng.lng]);
              onChange?.(e.latlng.lat, e.latlng.lng, address, pincode, city);
  
              getAddress(e.latlng.lat, e.latlng.lng).then((res) => {
                if (res) {
                  console.log(res);
                  setAddress(res.displayName);
                  setCity(res.address.city || res.address.town || "");
                  setPincode(res.address.postcode || "");
                }
              });
            },
          });
          return null;
        };
        
        setMapClickHandler(() => ClickHandlerComponent);
        setIsLeafletLoaded(true);
      } catch (error) {
        console.error("Failed to load Leaflet:", error);
      }
    };
    
    loadLeaflet();
  }, [onChange, address, pincode, city]);

  // 🔹 Get current geolocation on mount (client only)
  useEffect(() => {
    if (!isClient) return;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setMapCenter([latitude, longitude]);
          setPosition([latitude, longitude]);
          onChange?.(latitude, longitude, address, pincode, city);
        },
        () => {
          // fallback: keep initial center
          setPosition([initialLat, initialLng]);
        }
      );
    } else {
      setPosition([initialLat, initialLng]);
    }
  }, [isClient, initialLat, initialLng, onChange, address, pincode, city]);

  async function getAddress(lat: number, lng: number) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      return {
        displayName: data.display_name,
        address: data.address, // contains city, state, postcode, etc.
      };
    } catch (err) {
      console.error("Reverse geocoding failed", err);
      return null;
    }
  }

  // Don't render map on server or before leaflet is loaded
  if (!isClient || !isLeafletLoaded) {
    return (
      <div className="w-full h-[260px] relative rounded-xl overflow-hidden flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">Loading map...</div>
      </div>
    );
  }

  return (
    <div className="w-full h-[260px] relative rounded-xl overflow-hidden">
      <MapContainer
        center={mapCenter}
        zoom={initialZoom}
        className="w-full h-full"
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {MapClickHandler && <MapClickHandler />}

        {position && (
          <Marker
            position={position}
            draggable={true}
            eventHandlers={{
              dragend: (e: any) => {
                const marker = e.target;
                const { lat, lng } = marker.getLatLng();
                setPosition([lat, lng]);
                onChange?.(lat, lng, address, pincode, city);
              },
            }}
          />
        )}
      </MapContainer>

      {/* Controls */}
      <div className="absolute bottom-2 left-2 bg-white shadow p-2 rounded-xl space-y-2">
        <div className="flex gap-2">
          <input
            type="number"
            step="0.0001"
            placeholder="Latitude"
            value={position && Array.isArray(position) ? position[0] : ""}
            onChange={(e) => {
              const lat = parseFloat(e.target.value);
              if (!isNaN(lat) && position && Array.isArray(position)) {
                const newPos: [number, number] = [lat, position[1]];
                setPosition(newPos);
                onChange?.(newPos[0], newPos[1], address, pincode, city);
              }
            }}
            className="border p-1 rounded text-sm"
          />
          <input
            type="number"
            step="0.0001"
            placeholder="Longitude"
            value={position && Array.isArray(position) ? position[1] : ""}
            onChange={(e) => {
              const lng = parseFloat(e.target.value);
              if (!isNaN(lng) && position && Array.isArray(position)) {
                const newPos: [number, number] = [position[0], lng];
                setPosition(newPos);
                onChange?.(newPos[0], newPos[1], address, pincode, city);
              }
            }}
            className="border p-1 rounded text-sm"
          />
        </div>
        <button
          onClick={() => setPosition(null)}
          className="bg-red-500 text-white px-3 py-1 rounded w-full text-sm"
        >
          Clear Pin
        </button>
      </div>
    </div>
  );
};

export default MapPinPicker;


// "use client";
// import "../app/leaflet.css";

// import React, { useEffect, useState } from "react";
// import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
// import L, { LatLngExpression } from "leaflet";
// // import "@/styles/leaflet.css"; // <- Option B: your fixed CSS copy

// L.Marker.prototype.options.icon = L.icon({
//   iconUrl: "/leaflet/marker-icon.png",
//   iconRetinaUrl: "/leaflet/marker-icon-2x.png",
//   shadowUrl: "/leaflet/marker-shadow.png",
//   iconSize: [25, 41],
//   iconAnchor: [12, 41],
// });

// interface MapPinPickerProps {
//   initialLat?: number;
//   initialLng?: number;
//   initialZoom?: number;
//   address?: string;
//   pincode?: string;
//   city?: string;
//   state?: string;
//   country?: string;
//   onChange?: (
//     lat: number,
//     lng: number,
//     address: string,
//     pincode: string,
//     city: string
//   ) => void;
// }

// const MapPinPicker: React.FC<MapPinPickerProps> = ({
//   initialLat = 28.6139,
//   initialLng = 77.209,
//   initialZoom = 10,
//   onChange,
// }) => {
//   const [position, setPosition] = useState<LatLngExpression | null>(null);
//   const [mapCenter, setMapCenter] = useState<[number, number]>([
//     initialLat,
//     initialLng,
//   ]);
//   const [address, setAddress] = useState<string>("");
//   const [pincode, setPincode] = useState<string>("");
//   const [city, setCity] = useState<string>("");
//   // useEffect(()=>{
//   // console.log({position})
//   // },[])
//   // 🔹 Get current geolocation on mount
//   useEffect(() => {
//     if (navigator.geolocation) {
//       navigator.geolocation.getCurrentPosition(
//         (pos) => {
//           const { latitude, longitude } = pos.coords;
//           setMapCenter([latitude, longitude]);
//           setPosition([latitude, longitude]);
//           onChange?.(latitude, longitude, address, pincode, city);
//         },
//         () => {
//           // fallback: keep initial center
//           setPosition([initialLat, initialLng]);
//         }
//       );
//     } else {
//       setPosition([initialLat, initialLng]);
//     }
//   }, [initialLat, initialLng, onChange]);

//   async function getAddress(lat: number, lng: number) {
//     try {
//       const res = await fetch(
//         `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
//       );
//       const data = await res.json();
//       return {
//         displayName: data.display_name,
//         address: data.address, // contains city, state, postcode, etc.
//       };
//     } catch (err) {
//       console.error("Reverse geocoding failed", err);
//       return null;
//     }
//   }

//   const MapClickHandler: React.FC = () => {
//     useMapEvents({
//       click(e) {
//         setPosition([e.latlng.lat, e.latlng.lng]);
//         onChange?.(e.latlng.lat, e.latlng.lng, address, pincode, city);

//         getAddress(e.latlng.lat, e.latlng.lng).then((res) => {
//           if (res) {
//             console.log(res);
//             setAddress(res.displayName);
//             setCity(res.address.city || res.address.town || "");
//             setPincode(res.address.postcode || "");
//           }
//         });
//       },
//     });
//     return null;
//   };

//   return (
//     <div className="w-full h-[260px] relative rounded-xl overflow-hidden">
//       <MapContainer
//         center={mapCenter}
//         zoom={initialZoom}
//         className="w-full h-full"
//       >
//         <TileLayer
//           attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
//           url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//         />

//         <MapClickHandler />

//         {position && (
//           <Marker
//             position={position}
//             draggable={true}
//             eventHandlers={{
//               dragend: (e) => {
//                 const marker = e.target as L.Marker;
//                 const { lat, lng } = marker.getLatLng();
//                 setPosition([lat, lng]);
//                 onChange?.(lat, lng, address, pincode, city);
//               },
//             }}
//           />
//         )}
//       </MapContainer>

//       {/* Controls */}
//       <div className="absolute bottom-2 left-2 bg-white shadow p-2 rounded-xl space-y-2">
//         <div className="flex gap-2">
//           <input
//             type="number"
//             step="0.0001"
//             placeholder="Latitude"
//             value={position ? (position as [number, number])[0] : ""}
//             onChange={(e) => {
//               const lat = parseFloat(e.target.value);
//               if (!isNaN(lat) && position) {
//                 const newPos: [number, number] = [
//                   lat,
//                   (position as [number, number])[1],
//                 ];
//                 setPosition(newPos);
//                 onChange?.(newPos[0], newPos[1], address, pincode, city);
//               }
//             }}
//             className="border p-1 rounded"
//           />
//           <input
//             type="number"
//             step="0.0001"
//             placeholder="Longitude"
//             value={position ? (position as [number, number])[1] : ""}
//             onChange={(e) => {
//               const lng = parseFloat(e.target.value);
//               if (!isNaN(lng) && position) {
//                 const newPos: [number, number] = [
//                   (position as [number, number])[0],
//                   lng,
//                 ];
//                 setPosition(newPos);
//                 onChange?.(newPos[0], newPos[1], address, pincode, city);
//               }
//             }}
//             className="border p-1 rounded"
//           />
//         </div>
//         <button
//           onClick={() => setPosition(null)}
//           className="bg-red-500 text-white px-3 py-1 rounded w-full"
//         >
//           Clear Pin
//         </button>
//       </div>
//     </div>
//   );
// };

// export default MapPinPicker;
